import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, Checkbox, FormControl, FormHelperText, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { Group, Mail, Person } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';

import { LocalizationContext } from '@cvt/contexts';
import { emailRegex } from '@cvt/helpers/validation';

import { SearchAddressAutocomplete } from '@modules/Locations/components/SearchAddressAutocomplete';
import { getSpecializationLevelLabel, SPECIALIZATION_LEVELS } from '@modules/Specializations/helpers/specialization';
import { MajorAutocomplete } from '@modules/Majors/components/MajorAutocomplete';

import { StudyDestination, UserType } from '../enums';
import { PictureUploader } from '../components/PictureUploader';


type FormCrud = Omit<Users.Crud, 'location' | 'wantedMajor'> & {
  location: any;
  wantedMajor: CVT.MaybeNull<Majors.Major>;
  knowsWantedMajor: boolean;
};

type Fields = keyof (FormCrud & {
  submit: boolean;
});


interface Props {
  defaultValues?: Partial<FormCrud>;
  onSubmitRequest: (values: Users.Crud) => void;
  onSubmitButtonText: string;
  disabledFields?: Array<Fields>;
  hiddenFields?: Array<Fields>;
  slots?: {
    header?: React.ReactNode;
  };
}

const DEFAULT_VALUES: Partial<FormCrud> = {
  type: UserType.Customer,
  firstName: '',
  lastName: '',
  email: '',
};

export const UserForm: React.FC<Props> = ({ defaultValues = {}, onSubmitRequest, onSubmitButtonText, disabledFields, hiddenFields, slots }) => {

  const { dictionary } = React.useContext(LocalizationContext);

  const { handleSubmit, control, watch, formState: { isSubmitting, errors } } = useForm<FormCrud>({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
      knowsWantedMajor: !!defaultValues.wantedMajor,
    },
  });

  const isFieldDisabled = React.useCallback((field: Fields) => {
    if (!disabledFields) {
      return false;
    }
    return disabledFields.indexOf(field) !== -1;
  }, [disabledFields]);

  const isFieldVisible = React.useCallback((field: Fields) => {
    if (!hiddenFields) {
      return true;
    }
    return hiddenFields.indexOf(field) === -1;
  }, [hiddenFields]);

  const onSubmit = React.useCallback((data: FormCrud) => {
    const { knowsWantedMajor, wantedMajor, ...rest } = data;
    const wantedMajorId = typeof wantedMajor === 'number' ? wantedMajor : wantedMajor?.id;

    return onSubmitRequest({
      ...rest,
      wantedMajor: knowsWantedMajor ? wantedMajorId : null,
      location: data.location ? {
        title: data.location.name,
        latitude: data.location.location.lat,
        longitude: data.location.location.lng,
      } : null,
    });
  }, [onSubmitRequest]);

  const knowsWantedMajor = watch('knowsWantedMajor');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        {isFieldVisible('profilePicture') && (
          <Grid size={12} display="flex" justifyContent="center" alignItems="center">
            <Controller
              name="profilePicture"
              control={control}
              render={({ field, fieldState }) => (
                <PictureUploader
                  {...field}
                  value={field.value || undefined}
                  disabled={isFieldDisabled('profilePicture')}
                  label={dictionary.forms.user.fieldPicture}
                  error={!!fieldState.error}
                />
              )}
            />
          </Grid>
        )}
        {slots?.header && (
          <Grid size={12}>
            {slots.header}
          </Grid>
        )}
        {isFieldVisible('firstName') && (
          <Grid size={12} >
            <Controller
              name="firstName"
              control={control}
              rules={{ required: dictionary.forms.validations.required }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={dictionary.forms.user.fieldName}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Box ml={1} component={Person}/></InputAdornment>,
                  }}
                  disabled={isFieldDisabled('firstName')}
                />
              )}
            />
          </Grid>
        )}
        {isFieldVisible('lastName') && (
          <Grid size={12}>
            <Controller
              name="lastName"
              control={control}
              rules={{ required: dictionary.forms.validations.required }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={dictionary.forms.user.fieldLastName}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Box ml={1} component={Group}/></InputAdornment>,
                  }}
                  disabled={isFieldDisabled('lastName')}
                />
              )}
            />
          </Grid>
        )}
        {isFieldVisible('email') && (
          <Grid size={12}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: dictionary.forms.validations.required,
                pattern: {
                  value: emailRegex,
                  message: dictionary.forms.validations.invalidEmail,
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={dictionary.forms.fieldEmail}
                  error={!!errors.email}
                  helperText={!isFieldDisabled('email') && defaultValues.email ? dictionary.forms.user.fieldEmailDisabledHelper : errors.email?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Box ml={1} component={Mail}/></InputAdornment>,
                  }}
                  disabled={isFieldDisabled('email') || !!defaultValues.email}
                />
              )}
            />
          </Grid>
        )}
        {isFieldVisible('location') && (
          <Grid size={12}>
            <Controller
              name="location"
              control={control}
              render={({ field, fieldState }) => (
                <SearchAddressAutocomplete
                  {...field}
                  onChange={location => {
                    field.onChange(location);
                  }}
                  label={dictionary.forms.user.fieldLocation}
                  TextFieldProps={{
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                  }}
                  disabled={isFieldDisabled('location')}
                />
              )}
            />
          </Grid>
        )}
        {isFieldVisible('uniLocalisationPreference') && (
          <Grid size={12}>
            <Controller
              name="uniLocalisationPreference"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl size="small" fullWidth>
                  <InputLabel id="study-destination-select-label">{dictionary.forms.user.fieldWantedStudyDestination}</InputLabel>
                  <Select
                    {...field}
                    labelId="study-destination-select-label"
                    id="study-destination-select"
                    value={field.value ?? ''}
                    label={dictionary.forms.user.fieldWantedStudyDestination}
                    error={!!fieldState.error}
                  >
                    <MenuItem value={StudyDestination.Domestic}>{dictionary.forms.user.fieldWantedStudyDestinationDomestic}</MenuItem>
                    <MenuItem value={StudyDestination.Abroad}>{dictionary.forms.user.fieldWantedStudyDestinationAbroad}</MenuItem>
                  </Select>
                  {!!fieldState.error && (
                    <FormHelperText error>{fieldState.error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        )}
        {isFieldVisible('wantedDegreeLevel') && (
          <Grid size={12}>
            <Controller
              name="wantedDegreeLevel"
              control={control}
              rules={{ required: dictionary.forms.validations.required }}
              render={({ field, fieldState }) => (
                <FormControl size="small" fullWidth>
                  <InputLabel id="degree-select-label">{dictionary.forms.user.fieldWantedDegree}</InputLabel>
                  <Select
                    {...field}
                    labelId="degree-select-label"
                    id="degree-select"
                    value={field.value}
                    label={dictionary.forms.user.fieldWantedDegree}
                    error={!!fieldState.error}
                  >
                    {Object.values(SPECIALIZATION_LEVELS).map((opt) => (
                      <MenuItem
                        key={opt}
                        value={opt}
                        selected={field.value === opt}
                      >
                        {getSpecializationLevelLabel(dictionary, opt)}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!fieldState.error && (
                    <FormHelperText error>{fieldState.error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        )}
        {isFieldVisible('wantedMajor') && (
          <Grid size={12} container>
            <Grid size={12}>
              <Controller
                name="knowsWantedMajor"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" fullWidth sx={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disableRipple
                      disabled={isFieldDisabled('wantedMajor')}
                      id="knows-wanted-major-checkbox"
                    />
                    <InputLabel id="knows-wanted-major-label" htmlFor="knows-wanted-major-checkbox">{dictionary.forms.user.fieldKnowsWantedMajor}</InputLabel>
                  </FormControl>
                )}
              />
            </Grid>
            {!!knowsWantedMajor && (
              <Grid size={12}>
                <Controller
                  name="wantedMajor"
                  control={control}
                  render={({ field, fieldState }) => (
                    <MajorAutocomplete
                      {...field}
                      label={dictionary.forms.user.fieldWantedMajor}
                      size="small"
                      onChange={(value) => field.onChange(value)}
                      TextFieldProps={{
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message,
                      }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        )}
        {isFieldVisible('submit') && (
          <Grid size={12}>
            <Grid container justifyContent="space-between" spacing={2}>
              <Box ml="auto"/>
              <Grid size={12}>
                <LoadingButton
                  fullWidth
                  loading={isSubmitting}
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isFieldDisabled('submit')}
                >
                  {onSubmitButtonText}
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </form>
  );
};
